import React, { useEffect, useState } from 'react'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import { doc, getDoc } from 'firebase/firestore'

import { firestore } from '../../services/firebase/app'
import useUser from '../../services/useUser'
import useImpersonationSession from '../../hooks/useImpersonationSession'
import { callElevateImpersonation, callEndImpersonation, callLowerToView } from '../../services/permissions/impersonationService'
import ElevateDialog from './ElevateDialog'

/** m:ss, or h:mm:ss past the hour — this banner only ever shows minutes-scale (30m). */
function formatRemaining(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * #380 stage 4.1 (#1900) — §5 "the permanent indicator": always shows the
 * employer/user in context and the handler's own real identity, whatever
 * mode the session is in, with no dismiss short of ending it.
 *
 * Mounted once, route-independent (see App.js — same pattern as
 * `EmployerMessagingRegistration`) — "above every route" per §5, not a
 * per-screen concern.
 */
export default function ImpersonationBanner() {
  const user = useUser()
  const uid = user?.uid
  const state = useImpersonationSession(uid)
  const [employerName, setEmployerName] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState(null)

  const employerId = state.status === 'active' ? state.session?.employerId : null

  useEffect(() => {
    setEmployerName(null)
    if (employerId == null) return
    let cancelled = false
    getDoc(doc(firestore, 'employers', String(employerId)))
      .then((snapshot) => {
        if (!cancelled) setEmployerName(snapshot.exists() ? snapshot.data()?.name : null)
      })
      .catch(() => {
        if (!cancelled) setEmployerName(null)
      })
    return () => {
      cancelled = true
    }
  }, [employerId])

  // Loading is not a failure — it is every signed-in user's first tick on
  // every page load, admin or not. Only a real listener error (status
  // 'unknown') gets the blocking banner; a quiet 'loading'/'no-session' must
  // render nothing, per §5 "absence of the banner must mean no session".
  if (state.status === 'loading' || state.status === 'no-session') {
    return null
  }

  const runAction = async (fn) => {
    setBusy(true)
    setActionError(null)
    try {
      await fn()
      setDialogOpen(false)
    } catch (error) {
      setActionError(error?.message || 'הפעולה נכשלה')
    } finally {
      setBusy(false)
    }
  }

  if (state.status === 'unknown') {
    return (
      <Box
        role="banner"
        dir="rtl"
        sx={{
          position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.appBar + 1,
          bgcolor: 'error.main', color: 'error.contrastText', px: 2, py: 1,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography>מצב כניסה לתמיכה אינו ידוע — לא ניתן לוודא אם קיימת כניסת תמיכה פעילה.</Typography>
          <Stack direction="row" gap={1}>
            <Button size="small" variant="outlined" color="inherit" onClick={() => window.location.reload()}>
              ניסיון חוזר
            </Button>
            <Button
              size="small" variant="contained" color="inherit" disabled={busy}
              onClick={() => runAction(() => callEndImpersonation())}
            >
              יציאה
            </Button>
          </Stack>
        </Stack>
        {actionError && <Typography variant="caption">{actionError}</Typography>}
      </Box>
    )
  }

  const { session, effectiveMode, msRemaining } = state
  const employerLabel = employerName || `מעסיק ${session.employerId}`
  const targetLabel = session.uid ? `${employerLabel} · משתמש ${session.uid}` : employerLabel

  return (
    <Box
      role="banner"
      dir="rtl"
      sx={{
        position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.appBar + 1,
        bgcolor: effectiveMode === 'edit' ? 'warning.main' : 'info.main',
        color: effectiveMode === 'edit' ? 'warning.contrastText' : 'info.contrastText',
        px: 2, py: 1,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Chip
            size="small"
            label={effectiveMode === 'edit' ? 'עריכה' : 'צפייה'}
            color={effectiveMode === 'edit' ? 'default' : 'default'}
          />
          <Typography>
            כניסת תמיכה — {targetLabel}, בשם {user?.email || uid}
            {effectiveMode === 'edit' && msRemaining != null && ` (${formatRemaining(msRemaining)} לסיום מצב עריכה)`}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1}>
          {effectiveMode === 'view' && (
            <Button size="small" variant="outlined" color="inherit" onClick={() => setDialogOpen(true)}>
              מעבר לעריכה
            </Button>
          )}
          {effectiveMode === 'edit' && (
            <Button
              size="small" variant="outlined" color="inherit" disabled={busy}
              onClick={() => runAction(() => callLowerToView())}
            >
              חזרה לצפייה
            </Button>
          )}
          <Button
            size="small" variant="contained" color="inherit" disabled={busy}
            onClick={() => runAction(() => callEndImpersonation())}
          >
            יציאה מכניסת תמיכה
          </Button>
        </Stack>
      </Stack>
      {actionError && <Typography variant="caption">{actionError}</Typography>}

      <ElevateDialog
        open={dialogOpen}
        submitting={busy}
        errorMessage={actionError}
        employerLabel={targetLabel}
        onClose={() => {
          setActionError(null)
          setDialogOpen(false)
        }}
        onConfirm={({ reason, confirm }) => runAction(() => callElevateImpersonation({ reason, confirm }))}
      />
    </Box>
  )
}
